/**
 * Common utility functions for the scheduler module
 */

import type { ScheduledItem, Segment, SegmentPart, WorkOrder } from "../types";

/**
 * Day of week mapping (Sunday = 0, Monday = 1, etc.)
 */
export const DAY_OF_WEEK_MAP: { [key: string]: number } = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * Day names array (for iteration)
 */
export const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

/**
 * Day labels mapping (short form)
 */
export const DAY_LABELS: { [key: string]: string } = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

/**
 * Format hours as H.MM (e.g., 5.30 for 5 hours 30 minutes)
 */
export function formatHours(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}.${m.toString().padStart(2, "0")}`;
}

/**
 * Parse H.MM input (hours.minutes) to decimal hours.
 * Single digit after the dot = tens of minutes: "1.3" → 1h 30m = 1.5.
 * Two digits = minutes: "1.30" → 1h 30m = 1.5, "1.03" → 1h 3m.
 * Returns null if invalid.
 */
export function parseHoursFromHMM(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const [hPart, mPart] = trimmed.split(".");
  const hours = Number.parseInt(hPart ?? "0", 10);
  const minutesRaw = (mPart ?? "0").replace(/\s/g, "");
  if (Number.isNaN(hours) || hours < 0) return null;
  let minutes = Number.parseInt(minutesRaw, 10);
  if (Number.isNaN(minutes) || minutes < 0) return null;
  if (minutesRaw.length === 1) minutes *= 10; // "1.3" → 30 minutes
  minutes = Math.max(0, Math.min(59, minutes));
  return Number(((hours * 60 + minutes) / 60).toFixed(2));
}

/**
 * Get work order invoice number (e.g., "#129295")
 * Prioritizes invoiceNumber from database, falls back to workOrders array
 */
export function getWorkOrderNumber(
  item: ScheduledItem,
  workOrders: WorkOrder[],
): string {
  // Prioritize invoiceNumber from database (loaded from GetScheduleItems API)
  if (item.invoiceNumber) {
    return item.invoiceNumber;
  }

  // Fallback: Try to find from workOrders array
  const workOrder = workOrders.find((wo) => wo.id === item.workOrderId);
  return workOrder?.invoiceNumber || "Work Order";
}

/**
 * Get segment name from a scheduled item
 * Prioritizes displayText from database, falls back to workOrders lookup
 */
export function getSegmentName(
  item: ScheduledItem,
  workOrders: WorkOrder[],
  segmentParts: SegmentPart[],
): string {
  // First, try to use displayText from database (most reliable)
  if (item.displayText) {
    return item.displayText;
  }

  // Fallback: Try to find from workOrders array
  if (item.segmentId) {
    const workOrder = workOrders.find((wo) => wo.id === item.workOrderId);
    const segment = workOrder?.segmentList.find(
      (seg: Segment) => seg.id === item.segmentId,
    );
    return segment?.name || "Unknown segment";
  }

  if (item.segmentPartId) {
    const segmentPart = segmentParts.find(
      (part) => part.id === item.segmentPartId,
    );
    const workOrder = workOrders.find((wo) => wo.id === item.workOrderId);
    const segment = workOrder?.segmentList.find(
      (seg: Segment) => seg.id === segmentPart?.parentSegmentId,
    );
    return segment?.name || "Unknown segment";
  }

  return "Work order item";
}

/**
 * Calculate sortKey for inserting an item at a specific position
 * Uses decimal arithmetic to avoid recalculating all items
 * Position 0 = before first item, Position N = after item at index N-1
 */
export function calculateSortKey(
  items: ScheduledItem[],
  position: number,
): number {
  if (items.length === 0) return 1000.0;

  // Sort by sortKey (fallback to order if sortKey not set)
  const sorted = [...items].sort((a, b) => {
    const aKey = a.sortKey ?? a.order * 1000.0;
    const bKey = b.sortKey ?? b.order * 1000.0;
    return aKey - bKey;
  });

  if (position === 0) {
    // Insert at top: before first item
    const firstKey = sorted[0].sortKey ?? sorted[0].order * 1000.0;
    return firstKey - 1.0;
  } else if (position >= sorted.length) {
    // Insert at bottom: after last item
    const lastKey =
      sorted[sorted.length - 1].sortKey ?? sorted[sorted.length - 1].order * 1000.0;
    return lastKey + 1.0;
  } else {
    // Insert between items at position-1 and position
    const beforeKey = sorted[position - 1].sortKey ?? sorted[position - 1].order * 1000.0;
    const afterKey = sorted[position].sortKey ?? sorted[position].order * 1000.0;
    return (beforeKey + afterKey) / 2.0;
  }
}

/**
 * Get priority color based on priority string
 */
export function getPriorityColor(priority: string): "error" | "warning" | "default" | "info" {
  switch (priority) {
    case "high":
      return "error";
    case "medium":
      return "warning";
    case "low":
      return "default";
    default:
      return "default";
  }
}

/**
 * Get priority color based on work order ID (looks up priority from work orders)
 */
export function getPriorityColorFromWorkOrder(
  workOrderId: string,
  workOrders: WorkOrder[]
): "error" | "warning" | "info" | "inherit" {
  const wo = workOrders.find((wo) => wo.id === workOrderId);
  switch (wo?.priority) {
    case "high":
      return "error";
    case "medium":
      return "info";
    case "low":
      return "inherit";
    default:
      return "inherit";
  }
}

/**
 * Get date for a specific day in the current week
 * Simplified version - calculates from today
 */
export function getDateForDay(day: string): Date {
  const today = new Date();
  const currentDay = today.getDay();
  const targetDay = DAY_OF_WEEK_MAP[day] ?? 1;
  const diff = targetDay - currentDay;
  const date = new Date(today);
  date.setDate(date.getDate() + diff);
  return date;
}

/**
 * Get date for a specific day with week offset
 * Used in capacity board for week navigation
 * CRITICAL: Week starts on Sunday (weekday 0) to match EmployeeSchedule and LocationBusinessHours
 */
export function getDateForDayWithOffset(day: string, offset: number): Date {
  // Get current date and find Sunday of current week (week starts on Sunday)
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const currentSunday = new Date(today);
  currentSunday.setDate(today.getDate() - currentDay); // Go back to Sunday
  currentSunday.setHours(0, 0, 0, 0); // Reset to start of day
  
  // Apply week offset
  const weekStart = new Date(currentSunday);
  weekStart.setDate(currentSunday.getDate() + offset * 7);

  // Get the target day of week (0 = Sunday, 1 = Monday, etc.)
  const targetDayOfWeek = DAY_OF_WEEK_MAP[day] ?? 0;
  // Calculate days to add from Sunday
  const daysToAdd = targetDayOfWeek;
  const date = new Date(weekStart);
  date.setDate(weekStart.getDate() + daysToAdd);
  return date;
}

/**
 * Get day of week number from day string
 */
export function getDayOfWeek(day: string): number {
  return DAY_OF_WEEK_MAP[day] ?? 1;
}

/**
 * Get initials from a name
 */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Convert Tailwind color class to MUI color hex
 */
export function getAvatarColorFromClass(colorClass: string): string {
  if (colorClass.includes("blue")) return "#1976d2";
  if (colorClass.includes("green")) return "#2e7d32";
  if (colorClass.includes("orange")) return "#ed6c02";
  if (colorClass.includes("purple")) return "#9c27b0";
  if (colorClass.includes("pink")) return "#c2185b";
  return "#1976d2"; // default to primary blue
}

/**
 * Capitalize first letter of a string
 */
export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Get work order name by ID
 */
export function getWorkOrderName(workOrderId: string, workOrders: WorkOrder[]): string {
  return workOrders.find((wo) => wo.id === workOrderId)?.name || "Unknown";
}

/**
 * Get display name for a scheduled item
 * Handles segments, segment parts, and work orders
 * Uses displayText from database first, then falls back to workOrders lookup
 */
export function getItemDisplayName(
  item: ScheduledItem,
  workOrders: WorkOrder[],
  segmentParts: any[]
): string {
  // First, try to use displayText from database (most reliable, works even when workOrder not in current page)
  // displayText now contains only the segment name (no prefix)
  if (item.displayText) {
    return item.displayText;
  }
  
  // Fallback: Try to find from workOrders array (for backward compatibility)
  // If it's from a segment, show the segment name
  if (item.segmentId) {
    const workOrder = workOrders.find((wo) => wo.id === item.workOrderId);
    if (workOrder) {
      const segment = workOrder.segmentList.find((seg: Segment) => seg.id === item.segmentId);
      if (segment) {
        return segment.name;
      }
    }
  }
  // If it's from a segment part, find the parent segment and show its name
  if (item.segmentPartId) {
    const segmentPart = segmentParts.find((part) => part.id === item.segmentPartId);
    if (segmentPart) {
      const workOrder = workOrders.find((wo) => wo.id === item.workOrderId);
      if (workOrder) {
        const segment = workOrder.segmentList.find(
          (seg: Segment) => seg.id === segmentPart.parentSegmentId
        );
        if (segment) {
          return segment.name;
        }
      }
    }
  }
  // Otherwise, show the work order name
  return getWorkOrderName(item.workOrderId, workOrders);
}

/**
 * Format date string for display
 */
export function getDateString(day: string, offset: number): string {
  return getDateForDayWithOffset(day, offset).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Interpret a date/string as a local calendar day.
 * SQL and API values are calendar dates (often `YYYY-MM-DD` or `YYYY-MM-DDT00:00:00`);
 * parsing those as UTC then calling toISOString() shifts the day in non-UTC timezones.
 */
export function toLocalCalendarDate(
  value: Date | string | undefined | null,
): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
  }

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Compare two dates by local calendar day (ignores time / timezone string quirks).
 */
export function isSameLocalCalendarDay(
  a: Date | string | undefined | null,
  b: Date | string | undefined | null,
): boolean {
  const dateA = toLocalCalendarDate(a);
  const dateB = toLocalCalendarDate(b);
  if (!dateA || !dateB) return false;
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

/**
 * Interface for WorkOrderStatus from cache
 */
interface CachedWorkOrderStatus {
  identity: number;
  displayText: string;
  backColor?: string;
  BackColor?: string;
  foreColor?: string;
  ForeColor?: string;
}

/**
 * Get cached WorkOrderStatuses from localStorage
 * Cache key format: "WorkOrderStatuses_{username}"
 */
export function getCachedWorkOrderStatuses(): CachedWorkOrderStatus[] {
  try {
    const username = localStorage.getItem("username") || "currentUserIdentifier";
    const cacheKey = `WorkOrderStatuses_${username}`;
    const storedData = localStorage.getItem(cacheKey);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      return parsedData.data || parsedData || [];
    }
  } catch (error) {
    console.error("Error reading WorkOrderStatuses from cache:", error);
  }
  return [];
}

/**
 * Get the display name for a work order status
 * @param woStatusId - Work Order Status ID
 * @returns The display text from cached WorkOrderStatuses, or undefined if not found
 */
export function getWorkOrderStatusName(woStatusId: number | undefined): string | undefined {
  if (!woStatusId || woStatusId <= 0) {
    return undefined;
  }

  const statuses = getCachedWorkOrderStatuses();
  const status = statuses.find((s) => s.identity === woStatusId);
  
  if (status) {
    return status.displayText || undefined;
  }
  
  return undefined;
}

/**
 * Get the background color for a work order status
 * @param woStatusId - Work Order Status ID
 * @returns The background color from cached WorkOrderStatuses, or undefined if not found
 */
export function getWorkOrderStatusColor(woStatusId: number | undefined): string | undefined {
  if (!woStatusId || woStatusId <= 0) {
    return undefined;
  }

  const statuses = getCachedWorkOrderStatuses();
  const status = statuses.find((s) => s.identity === woStatusId);
  
  if (status) {
    return status.backColor || status.BackColor || undefined;
  }
  
  return undefined;
}

/**
 * Get the background color for a work order based on its status
 * Falls back to priority-based color if status color is not available
 * @param woStatusId - Work Order Status ID
 * @param priority - Priority string ("high", "medium", "low")
 * @returns The color to use for borderLeft styling
 */
export function getBorderLeftColor(
  woStatusId: number | undefined,
  priority?: string
): string {
  // First, try to get color from WorkOrderStatus
  const statusColor = getWorkOrderStatusColor(woStatusId);
  if (statusColor) {
    return statusColor;
  }
  
  // Fallback to priority-based color
  if (priority) {
    switch (priority) {
      case "high":
        return "#d32f2f"; // error.main equivalent
      case "medium":
        return "#ed6c02"; // warning.main equivalent
      default:
        return "#e0e0e0"; // divider equivalent
    }
  }
  
  return "#e0e0e0"; // divider equivalent
}
